/*
 * Display of the to-do "Verschobene Stunde KS021 22.10.: Lehrkraft klären" (course-manager, feat/moved-lesson-teacher).
 * The to-do carries a structured `source` (lesson move, course, dates, teacher names): the to-do shows a short explanation
 * and opens the decision dialog (MovedLessonTeacherDialog) directly. To-dos without source show their description as before.
 */
import moment from 'moment';
import {RawLocation} from 'vue-router/types/router';
import {MovedLessonTeacherToDoSource, ToDoItemSourceTypes} from '@/documentTypes/to-do-manager/ToDoItemSource';
import {DAY_FORMAT} from '@/helpers/timetable2/types';

type ToDoLike = { source?: { type?: string } | null } | null | undefined;

/** Lesson move as MovedLessonTeacherDialog needs it without a calendar event */
export type MovedLessonTeacherMove = {
    _id: string;
    course: string;
    courseCustomId: string;
    originalDate: string;
    newStart: string;
};

export const movedLessonTeacherSourceOf = (item: ToDoLike): MovedLessonTeacherToDoSource | null => {
    const source = item?.source as MovedLessonTeacherToDoSource | undefined;
    return source && source.type === ToDoItemSourceTypes.MOVED_LESSON_TEACHER && source.lessonMove && source.originalDate && source.newStart
        ? source : null;
};

/**
 * "Stunde vom 14.10. auf Do, 22.10., 15:00 Uhr verschoben – über einen Lehrkraftwechsel (ursprünglich Anna A, am neuen Termin
 * Bernd B). Bis zur Klärung wird sie niemandem abgerechnet." Names only with `withNames` (course teacher readable).
 */
export const movedLessonTeacherText = (source: MovedLessonTeacherToDoSource, withNames: boolean): string => {
    const start = moment(source.newStart);
    const names = withNames && (source.originalTeacherName || source.newTeacherName)
        ? ` (ursprünglich ${source.originalTeacherName || 'keine Lehrkraft'}, am neuen Termin ${source.newTeacherName || 'keine Lehrkraft'})` : '';
    return `Stunde vom ${moment(source.originalDate).format('DD.MM.')} auf ${start.format('dd, DD.MM., HH:mm')} Uhr verschoben – `
        + `über einen Lehrkraftwechsel${names}. Bis zur Klärung wird sie niemandem abgerechnet.`;
};

/** Standalone calendar at the week of the new date, filtered to the course chain */
export const movedLessonCalendarLink = (source: MovedLessonTeacherToDoSource): RawLocation => ({
    path: '/schedule',
    query: {date: moment(source.newStart).format(DAY_FORMAT), course: source.courseCustomId},
});

/** Input of MovedLessonTeacherDialog; null without the course (the teacher check needs it) */
export const movedLessonTeacherMove = (source: MovedLessonTeacherToDoSource): MovedLessonTeacherMove | null => source.course ? {
    _id: String(source.lessonMove),
    course: String(source.course),
    courseCustomId: source.courseCustomId,
    originalDate: moment(source.originalDate).toISOString(),
    newStart: moment(source.newStart).toISOString(),
} : null;
